<template>
  <div>
    <!-- Filtros -->
    <div class="mb-3">
      <div class="row">
        <div class="col-md-3">
          <label class="form-label">Estado AM (Auditoría Médica):</label>
          <select v-model="filtroAud" class="form-select form-select-sm">
            <option value="">Todos</option>
            <option value="PENDIENTE">PENDIENTE</option>
            <option value="REVISADA">REVISADA</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Estado GD (Gestión Documental):</label>
          <select v-model="filtroDoc" class="form-select form-select-sm">
            <option value="">Todos</option>
            <option value="PENDIENTE">PENDIENTE</option>
            <option value="REVISADA">REVISADA</option>
          </select>
        </div>
        <div class="col-md-2 d-flex align-items-end">
          <button @click="limpiarFiltros" class="btn btn-outline-secondary btn-sm w-100">
            Limpiar filtros
          </button>
        </div>
        <div class="col-md-2 d-flex align-items-end">
          <button
            @click="$emit('refrescar')"
            class="btn btn-outline-primary btn-sm w-100"
            :disabled="loading"
          >
            Refrescar
          </button>
        </div>
        <div class="col-md-2 d-flex align-items-end">
          <button
            @click="exportCsv"
            class="btn btn-outline-success btn-sm w-100"
            :disabled="loading || filasFiltradas.length === 0"
          >
            <span class="material-symbols-outlined" style="font-size: 16px; vertical-align: middle;">download</span>
            Exportar CSV
          </button>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="error" class="alert alert-danger">Error: {{ error }}</div>

    <!-- Spinner -->
    <div v-if="loading" class="text-center my-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <!-- Tabla -->
    <div v-if="!loading" class="table-responsive">
      <table class="table table-bordered table-sm">
        <thead class="table-light">
          <tr>
            <th>ID</th>
            <th>N°</th>
            <th>Cédula</th>
            <th>Nombre</th>
            <th>Estado AM</th>
            <th>Estado GD</th>
            <th>Desde</th>
            <th>Hasta</th>
            <th>Total</th>
            <th>Diagnósticos</th>
            <th>Códigos</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="p in filasPaginadas" :key="p.idPlanilla">
            <td>{{ p.idPlanilla }}</td>
            <td>{{ p.numero }}</td>
            <td>{{ p.cedula }}</td>
            <td>{{ p.nombre }}</td>
            <td :class="{ 'table-danger': p.estadoAud === 'PENDIENTE' }">{{ p.estadoAud }}</td>
            <td :class="{ 'table-danger': p.estadoDoc === 'PENDIENTE' }">{{ p.estadoDoc }}</td>
            <td>{{ formatDate(p.fechaDesde) }}</td>
            <td>{{ formatDate(p.fechaHasta) }}</td>
            <td class="text-end">{{ p.totalPlanilla }}</td>
            <td :class="{ 'table-warning': p.diagnosticos && p.diagnosticos.trim().length < 3 }">
              {{ p.diagnosticos }}
            </td>
            <td :class="{ 'table-warning': p.codigosDetalle && p.codigosDetalle.trim().length < 5 }">
              {{ p.codigosDetalle }}
            </td>
          </tr>
          <tr v-if="filasFiltradas.length === 0">
            <td colspan="11" class="text-center text-muted py-3">
              No hay resultados que coincidan.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Paginación -->
    <div class="d-flex justify-content-center mt-3 gap-2 align-items-center">
      <button
        class="btn btn-outline-secondary btn-sm"
        :disabled="paginaActual === 1"
        @click="paginaActual--"
      >
        Anterior
      </button>
      <span class="align-self-center">
        Página {{ paginaActual }} de {{ totalPaginas }}
      </span>
      <button
        class="btn btn-outline-secondary btn-sm"
        :disabled="paginaActual === totalPaginas"
        @click="paginaActual++"
      >
        Siguiente
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";

export default {
  name: "TablaResumen",
  props: {
    data:       { type: Array,   default: () => [] },
    loading:    { type: Boolean, default: false },
    error:      { type: String,  default: null },
    tabKey:     { type: String,  required: true },  // usado para nombre del CSV
    idTramite:  { type: Number,  required: true },  // usado para nombre del CSV
  },
  emits: ["refrescar"],
  setup(props) {
    // ── Filtros ───────────────────────────────────────────────────────────
    const filtroAud = ref("");
    const filtroDoc = ref("");

    const filasFiltradas = computed(() =>
      props.data.filter((p) => {
        const matchAud = filtroAud.value === "" || p.estadoAud === filtroAud.value;
        const matchDoc = filtroDoc.value === "" || p.estadoDoc === filtroDoc.value;
        return matchAud && matchDoc;
      })
    );

    const limpiarFiltros = () => {
      filtroAud.value  = "";
      filtroDoc.value  = "";
      paginaActual.value = 1;
    };

    // ── Paginación ────────────────────────────────────────────────────────
    const paginaActual   = ref(1);
    const itemsPorPagina = 10;

    const totalPaginas = computed(() =>
      Math.ceil(filasFiltradas.value.length / itemsPorPagina) || 1
    );

    const filasPaginadas = computed(() => {
      const ini = (paginaActual.value - 1) * itemsPorPagina;
      return filasFiltradas.value.slice(ini, ini + itemsPorPagina);
    });

    // ── Fecha ─────────────────────────────────────────────────────────────
    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      return isNaN(d) ? dateStr : d.toLocaleDateString();
    };

    // ── Exportar CSV ──────────────────────────────────────────────────────
    const exportCsv = () => {
      const headers = [
        "ID", "N°", "Cédula", "Nombre",
        "Estado AM", "Estado GD",
        "Desde", "Hasta", "Total",
        "Diagnósticos", "Códigos",
      ];

      const escape = (val) => {
        if (val === null || val === undefined) return "";
        const str = String(val).replace(/"/g, '""');
        return str.includes(";") || str.includes("\n") ? `"${str}"` : str;
      };

      const csvRows = [
        headers.join(";"),
        ...filasFiltradas.value.map((p) =>
          [
            p.idPlanilla,
            p.numero,
            p.cedula,
            p.nombre,
            p.estadoAud,
            p.estadoDoc,
            formatDate(p.fechaDesde),
            formatDate(p.fechaHasta),
            p.totalPlanilla,
            p.diagnosticos,
            p.codigosDetalle,
          ]
            .map(escape)
            .join(";")
        ),
      ];

      const bom     = "\uFEFF";
      const content = bom + csvRows.join("\n");
      const blob    = new Blob([content], { type: "text/csv;charset=utf-8;" });
      const url     = URL.createObjectURL(blob);
      const link    = document.createElement("a");
      link.href     = url;
      link.download = `tramite_${props.idTramite}_${props.tabKey}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    };

    return {
      filtroAud,
      filtroDoc,
      filasFiltradas,
      filasPaginadas,
      limpiarFiltros,
      paginaActual,
      totalPaginas,
      formatDate,
      exportCsv,
    };
  },
};
</script>